import {
  createCodeableConcept,
  DeviceMetricTypeCode,
  getFirstCodeFromCodableConcepts,
  getReferenceId,
} from '@quromedical/fhir-common';
import { getValidReference } from '../mappings/reference';
import type { Observation } from '../observation/schema';
import type { WithId } from '../types';

import {
  Command,
  type DeviceMetric,
  type DeviceMetricGetResponse,
} from './schema';

type DeviceMetricWithoutMeasurement = Omit<DeviceMetric, 'measurement'>;

export const mapDeviceMetricModelToFhir = (
  model: DeviceMetricWithoutMeasurement,
): fhir4.DeviceMetric => ({
  resourceType: 'DeviceMetric',
  category: model.category,
  type: createCodeableConcept(model.type),
  source: model.device,
  calibration: [
    {
      time: model.time,
      state: model.command.state,
      type: model.command.type,
    },
  ],
});

export const deviceMetricTypeToCommandMap: Record<
  DeviceMetricTypeCode,
  Command
> = {
  [DeviceMetricTypeCode.patchCalibration]: Command.PatchCalibrate,
};

export const commandToDeviceMetricTypeMap: Record<
  Command,
  DeviceMetricTypeCode
> = {
  [Command.PatchCalibrate]: DeviceMetricTypeCode.patchCalibration,
};

export const mapDeviceMetricFhirToModel = (
  fhir: WithId<fhir4.DeviceMetric>,
): DeviceMetricGetResponse => {
  const { id, category, type, source, calibration } = fhir;

  const command = calibration?.[0];
  const device = getValidReference(source);

  const typeCode = getFirstCodeFromCodableConcepts<DeviceMetricTypeCode>([
    type,
  ]);
  const commandType = typeCode
    ? deviceMetricTypeToCommandMap[typeCode]
    : undefined;

  return {
    id,
    category: category,
    time: command?.time,
    type: typeCode,
    command: {
      command: commandType,
      state: command?.state,
      type: command?.type,
    },
    device,
  };
};

export const attachMeasurementToDeviceMetric = (
  observations: WithId<Partial<Observation>>[],
): ((
  value: DeviceMetricGetResponse,
  index: number,
  array: DeviceMetricGetResponse[],
) => DeviceMetricGetResponse) => {
  return (deviceMetric) => {
    return {
      ...deviceMetric,
      // the deviceMetric is stored on the device field of the observation resource
      measurement: observations.find(
        (o) => getReferenceId(o.device) === deviceMetric.id,
      ),
    };
  };
};
